/**
 * SEMU Search Controller - Full-Width Header Search
 *
 * Modern full-width predictive search that slides down from header.
 * Uses Shopify's native predictive search endpoints with enhanced UX.
 *
 * @version 3.0.0
 * @author SEMU Theme
 */

class SEMUPredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.currentSearchTerm = '';
    this.abortController = new AbortController();
    this.allPredictiveSearchInstances = [];
    this._bootstrapped = false;
  }

  connectedCallback() {
    this._bootstrap = () => this.ensureInitialized();
    this.addEventListener('focusin', this._bootstrap);
    this._io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) this.ensureInitialized();
      },
      { rootMargin: '120px', threshold: 0 }
    );
    this._io.observe(this);
  }

  disconnectedCallback() {
    if (this._io) {
      this._io.disconnect();
      this._io = null;
    }
    this.removeEventListener('focusin', this._bootstrap);
  }

  ensureInitialized() {
    if (this._bootstrapped) return;
    this._bootstrapped = true;
    if (this._io) {
      this._io.disconnect();
      this._io = null;
    }
    this.removeEventListener('focusin', this._bootstrap);
    this.init();
  }

  init() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-predictive-search-results]');
    this.liveRegion = this.querySelector('[data-predictive-search-live-region]');

    if (!this.input) return;

    this.setupEventListeners();
    this.bindKeyboardEvents();
  }

  setupEventListeners() {
    this.input.addEventListener(
      'input',
      this.debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
  }

  bindKeyboardEvents() {
    this.input.addEventListener('keydown', (event) => {
      switch (event.code) {
        case 'ArrowUp':
          event.preventDefault();
          this.switchOption('up');
          break;
        case 'ArrowDown':
          event.preventDefault();
          this.switchOption('down');
          break;
        case 'Enter':
          this.selectOption();
          break;
        case 'Escape':
          this.close(true);
          this.input.blur();
          break;
      }
    });
  }

  onChange(event) {
    this.ensureInitialized();
    const searchTerm = event.target.value.trim();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFocus() {
    this.ensureInitialized();
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    }, 0);
  }

  getSearchResults(searchTerm) {
    this.ensureInitialized();
    const queryFormattedTerm = searchTerm.replace(' ', '-').toLowerCase();
    this.currentSearchTerm = searchTerm;

    if (this.cachedResults[queryFormattedTerm]) {
      this.renderSearchResults(this.cachedResults[queryFormattedTerm]);
      return;
    }

    // Show loading state
    this.showLoading();

    this.abortController.abort();
    this.abortController = new AbortController();

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=semu-predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const parsedHTML = new DOMParser().parseFromString(text, 'text/html');

        // Get the predictive search results container
        let resultsElement = parsedHTML.querySelector('.semu-predictive-search-results');

        // If not found, try alternate selectors
        if (!resultsElement) {
          resultsElement = parsedHTML.querySelector('#predictive-search-results');
        }

        if (!resultsElement) {
          resultsElement = parsedHTML.querySelector('[class*="predictive-search-results"]');
        }

        const resultsMarkup = resultsElement ? resultsElement.innerHTML : '';

        this.cachedResults[queryFormattedTerm] = resultsMarkup;
        this.hideLoading();
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.hideLoading();
        if (error?.code !== 20) {
          console.error('SEMU Search: Error fetching results', error);
          this.renderSearchResults(
            '<div class="semu-search-empty"><p>Search temporarily unavailable. Please try again.</p></div>'
          );
        }
      });
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Remove existing selection
    this.querySelectorAll('[aria-selected="true"]').forEach((element) => {
      element.setAttribute('aria-selected', false);
    });

    if (!selectedElement || (!moveUp && selectedElement.matches('li:last-child a, [role="option"]:last-child'))) {
      // Select first option
      const firstOption = this.querySelector('li:first-child a, [role="option"]:first-child');
      if (firstOption) {
        firstOption.setAttribute('aria-selected', true);
        firstOption.focus();
      }
      return;
    }

    if (moveUp && selectedElement.matches('li:first-child a, [role="option"]:first-child')) {
      // Return focus to search input
      this.input.focus();
      return;
    }

    // Navigate to next/previous option
    const nextElement = moveUp
      ? selectedElement.closest('li')?.previousElementSibling?.querySelector('a') ||
        selectedElement.previousElementSibling
      : selectedElement.closest('li')?.nextElementSibling?.querySelector('a') || selectedElement.nextElementSibling;

    if (nextElement) {
      nextElement.setAttribute('aria-selected', true);
      nextElement.focus();
    }
  }

  selectOption() {
    const selectedElement = this.querySelector('[aria-selected="true"]');
    if (selectedElement) {
      selectedElement.click();
    } else if (this.input.value.trim()) {
      // Submit search form
      window.location.href = `${routes.search_url}?q=${encodeURIComponent(this.input.value.trim())}`;
    }
  }

  renderSearchResults(resultsMarkup) {
    if (!this.results) {
      console.error('SEMU Search: Results container not found');
      return;
    }

    this.results.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionText(this.results.textContent);
    this.open();
  }

  setLiveRegionText(statusText) {
    if (!this.liveRegion) return;

    this.liveRegion.setAttribute('aria-hidden', 'false');
    this.liveRegion.textContent = statusText;

    setTimeout(() => {
      this.liveRegion.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);

    // Remove aria-selected from all options
    this.querySelectorAll('[aria-selected="true"]').forEach((element) => {
      element.setAttribute('aria-selected', false);
    });
  }

  showLoading() {
    const loadingElement = this.querySelector('[data-predictive-search-loading]');
    if (loadingElement) {
      loadingElement.style.display = 'flex';
    }
    if (this.results) {
      this.results.setAttribute('data-loading', 'true');
    }
  }

  hideLoading() {
    const loadingElement = this.querySelector('[data-predictive-search-loading]');
    if (loadingElement) {
      loadingElement.style.display = 'none';
    }
    if (this.results) {
      this.results.removeAttribute('data-loading');
    }
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define('semu-predictive-search', SEMUPredictiveSearch);

/**
 * Search Modal Component - Full Width Header Search
 */
class SEMUSearchModal extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.isAnimating = false;
    this.searchTimeout = null;
  }

  connectedCallback() {
    this.setupModal();
    this.bindEvents();
    this.setupHeaderIntegration();
  }

  setupModal() {
    this.modal = this.querySelector('.semu-search-modal');
    this.backdrop = this.querySelector('[data-search-backdrop]');
    this.closeButton = this.querySelector('[data-search-close]');
    this.form = this.querySelector('[data-search-form]');
    this.input = this.form?.querySelector('input[type="text"], input[type="search"]');
    this.searchContent = this.querySelector('[data-search-content]');
    this.popularSearches = this.querySelectorAll('[data-search-term]');

    // Find predictive search component
    this.predictiveSearch = this.querySelector('semu-predictive-search');
    if (this.predictiveSearch) {
      this.predictiveSearch.input = this.input;
    }
  }

  bindEvents() {
    // Open search modal
    document.addEventListener('semu:search:open', () => this.open());
    document.addEventListener('keydown', (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        this.open();
      }

      if (event.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Close events
    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => this.close());
    }

    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.close());
    }

    // Removed clear button functionality for cleaner design

    // Form submission
    if (this.form) {
      this.form.addEventListener('submit', (event) => {
        const query = this.input?.value?.trim();
        if (!query) {
          event.preventDefault();
        }
      });
    }

    // Input event listener for predictive search
    if (this.input) {
      this.input.addEventListener('input', () => {
        const query = this.input.value.trim();

        // Show/hide content based on input
        if (this.searchContent) {
          if (query) {
            this.searchContent.classList.remove('semu-search-content--hidden');
            this.searchContent.classList.add('semu-search-content--visible');
          } else {
            this.searchContent.classList.add('semu-search-content--hidden');
            this.searchContent.classList.remove('semu-search-content--visible');
          }
        }

        // Trigger predictive search
        if (this.predictiveSearch && query) {
          this.predictiveSearch.getSearchResults(query);
        } else if (this.predictiveSearch) {
          this.predictiveSearch.close(true);
        }
      });
    }

    // Popular search click events
    this.popularSearches.forEach((button) => {
      button.addEventListener('click', () => {
        const searchTerm = button.getAttribute('data-search-term');
        if (searchTerm && this.input) {
          this.input.value = searchTerm;
          this.input.dispatchEvent(new Event('input', { bubbles: true }));
          this.input.focus();
        }
      });
    });
  }

  open() {
    if (this.isAnimating || this.isOpen) return;

    this.isAnimating = true;
    this.isOpen = true;

    // Update header height before opening
    this.updateHeaderHeight();

    // Update aria attributes
    this.modal?.setAttribute('aria-expanded', 'true');

    // Add active class with animation
    this.modal?.classList.add('is-active');
    this.classList.add('search-is-active'); // 'this' is the semu-search-modal element itself
    document.body.classList.add('search-is-open');

    // Focus input after animation
    setTimeout(() => {
      this.input?.focus();
      this.isAnimating = false;
    }, 400);

    // Update header state
    this.updateHeaderState(true);

    // Dispatch event
    document.dispatchEvent(new CustomEvent('semu:search:opened'));
  }

  close() {
    if (this.isAnimating || !this.isOpen) return;

    this.isAnimating = true;
    this.isOpen = false;

    // Update aria attributes
    this.modal?.setAttribute('aria-expanded', 'false');

    // Remove active class
    this.modal?.classList.remove('is-active');
    this.classList.remove('search-is-active'); // 'this' is the semu-search-modal element itself
    document.body.classList.remove('search-is-open');

    // Clear search after animation
    setTimeout(() => {
      if (this.input) {
        this.input.value = '';
      }
      this.clearResults();

      // Reset content visibility
      if (this.searchContent) {
        this.searchContent.classList.add('semu-search-content--hidden');
        this.searchContent.classList.remove('semu-search-content--visible');
      }

      this.isAnimating = false;
    }, 400);

    // Update header state
    this.updateHeaderState(false);

    // Dispatch event
    document.dispatchEvent(new CustomEvent('semu:search:closed'));
  }

  // Removed updateClearButton method as clear button is removed

  setupHeaderIntegration() {
    // Listen for header search button clicks
    document.addEventListener('click', (event) => {
      const searchTrigger = event.target.closest('[data-search-trigger]');
      if (searchTrigger) {
        event.preventDefault();
        this.open();
      }
    });
  }

  updateHeaderState(isOpen) {
    const header = document.querySelector('.header');
    if (header) {
      if (isOpen) {
        header.classList.add('search-active');
      } else {
        header.classList.remove('search-active');
      }
    }
  }

  updateHeaderHeight() {
    // Get all header group elements (announcement bar + header)
    const headerGroup = document.querySelector('.shopify-section-group-header-group');

    if (headerGroup) {
      // Use the entire header group height which includes announcement bar if present
      const totalHeight = headerGroup.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--header-height', `${totalHeight}px`);
    } else {
      // Fallback: calculate separately if header group not found
      const announcementBar = document.querySelector('.announcement-bar-section, .utility-bar');
      const headerWrapper = document.querySelector('.header-wrapper');

      let totalHeight = 0;

      // Add announcement bar height if it exists and visible
      if (announcementBar && announcementBar.offsetHeight > 0) {
        totalHeight += announcementBar.getBoundingClientRect().height;
      }

      // Add header height
      if (headerWrapper) {
        totalHeight += headerWrapper.getBoundingClientRect().height;
      }

      // Set the total height as CSS variable
      if (totalHeight > 0) {
        document.documentElement.style.setProperty('--header-height', `${totalHeight}px`);
      }
    }
  }

  clearResults() {
    if (this.predictiveSearch && this.predictiveSearch.results) {
      this.predictiveSearch.results.innerHTML = '';
      this.predictiveSearch.removeAttribute('results');
    }
  }
}

customElements.define('semu-search-modal', SEMUSearchModal);

// Global search methods
window.SEMU = window.SEMU || {};
window.SEMU.openSearch = () => {
  document.dispatchEvent(new CustomEvent('semu:search:open'));
};
window.SEMU.closeSearch = () => {
  document.dispatchEvent(new CustomEvent('semu:search:close'));
};

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeSearch);
} else {
  initializeSearch();
}

function initializeSearch() {
  if (typeof routes === 'undefined') {
    window.routes = window.routes || {
      search_url: '/search',
      predictive_search_url: '/search/suggest',
    };
  }

  let heightRaf = null;
  const updateHeaderHeight = () => {
    const headerGroup = document.querySelector('.shopify-section-group-header-group');

    if (headerGroup) {
      const totalHeight = headerGroup.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--header-height', `${totalHeight}px`);
    } else {
      const announcementBar = document.querySelector('.announcement-bar-section, .utility-bar');
      const headerWrapper = document.querySelector('.header-wrapper');

      let totalHeight = 0;

      if (announcementBar && announcementBar.offsetHeight > 0) {
        totalHeight += announcementBar.getBoundingClientRect().height;
      }

      if (headerWrapper) {
        totalHeight += headerWrapper.getBoundingClientRect().height;
      }

      if (totalHeight > 0) {
        document.documentElement.style.setProperty('--header-height', `${totalHeight}px`);
      }
    }
  };

  const scheduleHeaderHeight = () => {
    if (heightRaf) cancelAnimationFrame(heightRaf);
    heightRaf = requestAnimationFrame(() => {
      heightRaf = null;
      updateHeaderHeight();
    });
  };

  updateHeaderHeight();
  window.addEventListener('resize', scheduleHeaderHeight, { passive: true });
  window.addEventListener('scroll', scheduleHeaderHeight, { passive: true });
  window.addEventListener('load', scheduleHeaderHeight);

  const headerGroup = document.querySelector('.shopify-section-group-header-group');
  if (headerGroup && typeof ResizeObserver !== 'undefined') {
    const ro = new ResizeObserver(scheduleHeaderHeight);
    ro.observe(headerGroup);
  }

  let mutationT = null;
  const observer = new MutationObserver(() => {
    clearTimeout(mutationT);
    mutationT = setTimeout(scheduleHeaderHeight, 100);
  });
  if (headerGroup) {
    observer.observe(headerGroup, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
  }
}
